'use client';

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, X, Music2, ChevronDown } from 'lucide-react';
import type { Song } from '@/lib/songs';

type Props = {
  songs: Song[];
  selected: Song[];
  onSelect: (song: Song) => void;
  onRemove: (id: string) => void;
  multi?: boolean;
  placeholder?: string;
  error?: string;
};

const SOURCE_LABELS: Record<Song['source'], string> = {
  pitchfork500: 'Pitchfork 500',
  punk: 'Greatest Punk',
};

const SOURCE_COLORS: Record<Song['source'], string> = {
  pitchfork500: 'bg-[#f5c842]/15 text-[#f5c842] border-[#f5c842]/30',
  punk: 'bg-[#e8386d]/15 text-[#e8386d] border-[#e8386d]/30',
};

export default function SongSearch({
  songs,
  selected,
  onSelect,
  onRemove,
  multi = false,
  placeholder = 'Search songs or artists…',
  error,
}: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<'all' | Song['source']>('all');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return songs
      .filter((s) => {
        if (sourceFilter !== 'all' && s.source !== sourceFilter) return false;
        if (!q) return true;
        return s.title.toLowerCase().includes(q) || s.artist.toLowerCase().includes(q);
      })
      .slice(0, 40);
  }, [songs, query, sourceFilter]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(song: Song) {
    if (selectedIds.has(song.id)) {
      onRemove(song.id);
    } else {
      onSelect(song);
      if (!multi) {
        setOpen(false);
        setQuery('');
      }
    }
  }

  return (
    <div className="space-y-3">
      {/* Selected tags */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((s) => (
            <span
              key={`selected-${s.id}`}
              className="flex items-center gap-1.5 bg-[#1e1e1e] border border-[#333] text-[#f0ece3] text-xs font-medium px-3 py-1.5 rounded-full"
            >
              <Music2 size={11} className="text-[#f5c842]" />
              <span className="max-w-[180px] truncate">{s.title}</span>
              <span className="text-[#666]">—</span>
              <span className="text-[#888] max-w-[100px] truncate">{s.artist}</span>
              <button
                type="button"
                onClick={() => onRemove(s.id)}
                className="ml-1 text-[#666] hover:text-[#e8386d] transition-colors"
                aria-label={`Remove ${s.title}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div ref={containerRef} className="relative">
        <div
          className={`flex items-center gap-2 bg-[#141414] border rounded px-3 py-2.5 transition-colors ${
            error ? 'border-[#e8386d]' : open ? 'border-[#f5c842]' : 'border-[#2e2e2e] hover:border-[#444]'
          }`}
        >
          <Search size={15} className="text-[#555] shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-[#f0ece3] text-sm placeholder-[#555] focus:outline-none"
          />
          {/* Source filter */}
          <div className="relative shrink-0">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as typeof sourceFilter)}
              className="appearance-none bg-[#1e1e1e] border border-[#333] text-[#888] text-xs rounded px-2 py-1 pr-5 focus:outline-none focus:border-[#f5c842] cursor-pointer"
            >
              <option value="all">All Lists</option>
              <option value="pitchfork500">Pitchfork 500</option>
              <option value="punk">Greatest Punk</option>
            </select>
            <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#666] pointer-events-none" />
          </div>
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setOpen(false); }}
              className="text-[#555] hover:text-[#f0ece3] transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {open && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50 max-h-72 overflow-y-auto scrollbar-thin">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-[#555] text-sm">
                No songs match &ldquo;{query}&rdquo;
              </div>
            ) : (
              <ul>
                {filtered.map((song) => {
                  const isSelected = selectedIds.has(song.id);
                  return (
                    <li key={`option-${song.id}`}>
                      <button
                        type="button"
                        onClick={() => handleSelect(song)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          isSelected
                            ? 'bg-[#f5c842]/10 text-[#f5c842]'
                            : 'text-[#f0ece3] hover:bg-[#242424]'
                        }`}
                      >
                        <Music2 size={13} className={isSelected ? 'text-[#f5c842]' : 'text-[#555]'} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{song.title}</p>
                          <p className="text-xs text-[#666] truncate">{song.artist} · {song.year}</p>
                        </div>
                        <span
                          className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${SOURCE_COLORS[song.source]}`}
                        >
                          {SOURCE_LABELS[song.source]}
                        </span>
                        {isSelected && (
                          <span className="shrink-0 w-4 h-4 rounded-full bg-[#f5c842] flex items-center justify-center">
                            <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                              <path d="M1 3L3 5L7 1" stroke="#0f0f0f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {error && <p className="error-text">{error}</p>}
      {!error && multi && (
        <p className="helper-text">Select as many songs as you know — the more the better for matching.</p>
      )}
    </div>
  );
}